import { Injectable } from '@angular/core';

import moment, { Moment } from 'moment-timezone';
import { Observable, map, of, tap } from 'rxjs';

import { MentionsApi } from '../../../api/mentions/mentions.api';
import { Mention } from '../../Mentions/models/mention';
import { TVEyesPlayerletteUrl } from '../../Mentions/models/tveyes-playerlette-url.model';
import { TVEyesMentionsService } from '../../Mentions/services/tveyes-mentions.service';
import { TVEyesIFrameWindowMessageAction } from '../components/TVEyesMediaViewDialog/tveyes-iframe-window-message.model';
import { TVEyesTimeline, TVEyesTimelineDateTimesForPage, TVEyesTimelineThumbnail } from '../components/TVEyesMediaViewDialog/tveyes-timeline.model';

@Injectable()
export class TVEyesTimelineService {
    private static THUMBNAIL_TIME_DIFFERENCE = 60; // Size of api return * 10
    private static END_DATE_WITH_CONTEXT_MINUTES = 3; // Front end returns the start date with 3 minutes of pre-context
    private static START_DATETIME_FORMAT = 'YYYY-MM-DD+HH:mm:ss';

    private timeline: TVEyesTimeline;

    constructor(
        private thumbnailApi: MentionsApi,
        private tvEyesMentionsService: TVEyesMentionsService,
    ) {}

    createTimeline(mention: Mention, iframeUrl: string, keywords?: string[]) {
        const baseClipStartDate = this.getStartDateTimeFromMediaviewUrl(iframeUrl);
        this.timeline = new TVEyesTimeline(
            mention,
            baseClipStartDate,
            keywords,
        );
    }

    fetchInitialThumbnails(): Observable<TVEyesTimelineThumbnail[]> {
        return this.fetchThumbnails(this.timeline.getBaseClipStartDateTime()).pipe(
            tap(thumbnails => this.timeline.setTimelineThumbnailsOnPage(0, thumbnails))
        );
    }

    getPodcastThumbnailsPyPage(page: number): Observable<TVEyesTimelineThumbnail[]> {
        const dateTimesForPage = this.getDateTimesForPage(page);
        const thumbnails: TVEyesTimelineThumbnail[] = [];
        thumbnails.push({
            datetime: dateTimesForPage.start.toISOString(),
            url: null
        });

        let startTime = dateTimesForPage.start;
        for (let i = 1; i < 6; i++) {
            startTime = moment.utc(startTime, TVEyesTimelineService.START_DATETIME_FORMAT).add(10, 'seconds');
            thumbnails.push({
                datetime: startTime.toISOString(),
                url: null
            });
        }
        return of(thumbnails);
    }

    getThumbnailsByPage(page: number): Observable<TVEyesTimelineThumbnail[]> {
        const existingThumbnails = this.timeline.getTimelineThumbnailsByPage(page);
        if (existingThumbnails) {
            return of(existingThumbnails);
        }

        const dateTimesForPage = this.getDateTimesForPage(page);
        return this.fetchThumbnails(dateTimesForPage.start).pipe(
            tap(thumbnails => this.timeline.setTimelineThumbnailsOnPage(page, thumbnails))
        );
    }

    getRefreshUrl(dateTime: string, action: TVEyesIFrameWindowMessageAction): Observable<string> {
        switch (action) {
            case 'setPlayhead':
            case 'setStartPoint':
                const newStartDate = moment.utc(dateTime);
                if (newStartDate.isBefore(this.timeline.getCurrentClip().getStartDateTime())
                    || newStartDate.isAfter(this.timeline.getCurrentClip().getMediaViewEndDateTime())
                ) {
                    const newPlayerletteUrl = new TVEyesPlayerletteUrl(
                        this.timeline.getCurrentClip().getMentionUrl(),
                        newStartDate,
                        this.timeline.getCurrentClip().getDuration(),
                        this.timeline.getCurrentClip().getStationId()
                    ).getMentionUrl();

                    this.timeline.setCurrentClip(TVEyesPlayerletteUrl.fromUrl(newPlayerletteUrl));
                    return this.tvEyesMentionsService.getMediaViewUrlFromPlayerlette(
                        this.timeline.getOriginalMention(),
                        newPlayerletteUrl,
                        this.timeline.getOriginalKeywords(),
                        false
                    ).pipe(
                        tap(url => this.timeline.getCurrentClip().setStartDateTime(this.getStartDateTimeFromMediaviewUrl(url)))
                    );
                }
                break;
            case 'setEndPoint':
                const newEndDate = moment.utc(dateTime);
                const dateDiff = newEndDate.diff(this.timeline.getCurrentClip().getMediaViewEndDateTime(), 'seconds');
                if (newEndDate.isAfter(this.timeline.getCurrentClip().getMediaViewEndDateTime())
                    || newEndDate.isBefore(this.timeline.getCurrentClip().getStartDateTime())
                ) {
                    const newPlayerletteUrl = new TVEyesPlayerletteUrl(
                        this.timeline.getCurrentClip().getMentionUrl(),
                        moment.utc(this.timeline.getCurrentClip().getStartDateTime())
                            .add(dateDiff, 'seconds')
                            .add(TVEyesTimelineService.END_DATE_WITH_CONTEXT_MINUTES, 'minutes'),
                        this.timeline.getCurrentClip().getDuration(),
                        this.timeline.getCurrentClip().getStationId()
                    ).getMentionUrl();

                    this.timeline.setCurrentClip(TVEyesPlayerletteUrl.fromUrl(newPlayerletteUrl));
                    return this.tvEyesMentionsService.getMediaViewUrlFromPlayerlette(
                        this.timeline.getOriginalMention(),
                        newPlayerletteUrl,
                        this.timeline.getOriginalKeywords(),
                        false
                    ).pipe(
                        tap(url => this.timeline.getCurrentClip().setStartDateTime(this.getStartDateTimeFromMediaviewUrl(url)))
                    );
                }
                break;
        }
        return of(null);
    }

    private getDateTimesForPage(page: number): TVEyesTimelineDateTimesForPage {
        const timeDiff = page * TVEyesTimelineService.THUMBNAIL_TIME_DIFFERENCE;
        const startDateTime = moment.utc(this.timeline.getBaseClipStartDateTime()).add(timeDiff, 'seconds');
        const endDateTime = moment.utc(startDateTime).add(TVEyesTimelineService.THUMBNAIL_TIME_DIFFERENCE, 'seconds');
        return {
            start: startDateTime,
            end: endDateTime
        };
    }

    private fetchThumbnails(startDateTime: Moment): Observable<TVEyesTimelineThumbnail[]> {
        const dateTime = moment.utc(startDateTime).toISOString();
        return this.thumbnailApi.fetchTVEyesTimelineThumbnails(this.timeline.getOriginalMention().url, dateTime).pipe(
            map(results => {
                const thumbnails: TVEyesTimelineThumbnail[] = results.map(result => {
                    return {
                        datetime: moment.utc(result.dateTime, TVEyesTimelineService.START_DATETIME_FORMAT).toISOString(),
                        url: result.thumbnailUrl
                    };
                });
                return thumbnails;
            })
        );
    }

    private getStartDateTimeFromMediaviewUrl(encodedMediaviewUrl: string): Moment {
        const url = new URL(encodedMediaviewUrl);
        const encodedParam = url.search.substring(1);
        const decodedParam = atob(encodedParam);
        url.search = decodedParam;
        const urlModel = TVEyesPlayerletteUrl.fromUrl(url.toString());
        return urlModel.getStartDateTime();
    }
}
